// File: src/components/admin/plans/PlanDiffDialog.js
// AC14/15: confirm-changes modal before Save propagates a plan
// edit to every user on the plan.
//
// Renders: field-by-field diff, user count, side-effect callouts.
// Cancel preserves the form. Confirm fires the supplied onConfirm callback.

import React, { useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Table, TableBody, TableCell, TableHead, TableRow,
    Alert, Typography, Stack, Box, CircularProgress,
} from '@mui/material';

function formatValue(v) {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    if (Array.isArray(v)) return v.length === 0 ? '[]' : `[${v.map(x => `"${x}"`).join(', ')}]`;
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
}

const CAP_FIELDS = new Set([
    'monthlyAiActions', 'activeCampaigns', 'storageMb', 'apiEnabled', 'apiMonthlyRequests',
]);

export default function PlanDiffDialog({
    open, planId, planLabel, before, after, userCount, saving, onCancel, onConfirm,
}) {
    const diff = useMemo(() => {
        if (!before || !after) return [];
        const out = [];
        const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
        for (const k of keys) {
            if (['id', 'archived'].includes(k)) continue;
            const a = before[k];
            const b = after[k];
            if (JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)) {
                out.push({ field: k, from: a, to: b });
            }
        }
        return out;
    }, [before, after]);

    const sideEffects = useMemo(() => {
        const callouts = [];
        for (const d of diff) {
            if (CAP_FIELDS.has(d.field)) {
                const fromN = Number(d.from);
                const toN = Number(d.to);
                if (Number.isFinite(fromN) && Number.isFinite(toN) && toN < fromN && toN !== 0) {
                    callouts.push({
                        severity: 'warning',
                        text: `Lowers ${d.field} cap from ${fromN} → ${toN}. ` +
                              `Users on this plan above the new cap will see soft upgrade modals ` +
                              `on their next cost-bearing action.`,
                    });
                }
            }
            if (d.field === 'stripePriceId') {
                callouts.push({
                    severity: 'info',
                    text: 'Changes Stripe Price ID. Existing subscriptions are unaffected. ' +
                          'New checkouts will use the new Price.',
                });
            }
            if (d.field === 'price') {
                const stripePriceChanged = diff.some(x => x.field === 'stripePriceId');
                if (!stripePriceChanged) {
                    callouts.push({
                        severity: 'warning',
                        text: 'Display price changed but Stripe Price unchanged — Stripe will still charge ' +
                              'the old amount. Did you mean to pick a new Price too?',
                    });
                }
            }
        }
        return callouts;
    }, [diff]);

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onCancel}
            maxWidth="md"
            fullWidth
            data-testid={`plan-diff-dialog-${planId}`}
        >
            <DialogTitle>Review changes before applying</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Editing plan <b>{planLabel}</b> ({planId})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" data-testid={`plan-diff-user-count-${planId}`}>
                            {userCount === -1
                                ? 'Users on this plan: unknown'
                                : (userCount === 1
                                    ? '1 user is currently on this plan'
                                    : `${userCount} users are currently on this plan`)}
                        </Typography>
                    </Box>

                    {diff.length === 0 ? (
                        <Alert severity="info">No fields changed.</Alert>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {diff.map((d) => (
                                    <TableRow key={d.field} data-testid={`plan-diff-row-${planId}-${d.field}`}>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{d.field}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{formatValue(d.from)}</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{formatValue(d.to)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {sideEffects.map((s, i) => (
                        <Alert key={i} severity={s.severity}>{s.text}</Alert>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={saving} data-testid={`plan-diff-cancel-${planId}`}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    disabled={saving || diff.length === 0}
                    data-testid={`plan-diff-confirm-${planId}`}
                >
                    {saving ? <CircularProgress size={20} /> : 'Apply changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
